import React, { useEffect, useState } from "react";
import { MapPin, Calendar as CalendarIcon, Search, ArrowRight } from "lucide-react";
import { HERO_WORDS, LOCATIONS, STATS } from "../mock";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { format } from "date-fns";
import { Award, Map, Stamp, Users } from "lucide-react";

const STAT_ICONS = [Award, Map, Stamp, Users];

export default function Hero() {
  const [wordIdx, setWordIdx] = useState(0);
  const [location, setLocation] = useState("");
  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();

  useEffect(() => {
    const id = setInterval(() => {
      setWordIdx((i) => (i + 1) % HERO_WORDS.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="top" className="relative pt-[84px]">
      {/* Background */}
      <div className="relative h-[640px] md:h-[720px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=1920&q=80"
          alt="Beautiful travel destination"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(31,91,99,0.55) 0%, rgba(31,91,99,0.30) 45%, rgba(31,91,99,0.75) 100%)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 h-full max-w-6xl mx-auto px-5 lg:px-8 flex flex-col items-center justify-center text-center">
          <p className="uppercase tracking-[0.35em] text-white/80 text-xs md:text-sm mb-4">
            Beyond Travel, Into Stories
          </p>
          <h1
            className="text-white text-4xl md:text-6xl font-light leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Where Every Trip Becomes a
          </h1>
          <div className="h-[64px] md:h-[88px] mt-2 md:mt-3 overflow-hidden">
            <h2
              key={HERO_WORDS[wordIdx]}
              className="text-4xl md:text-6xl font-extrabold text-[#F5C05B] animate-[fadeSlide_2.2s_ease_infinite]"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
            >
              {HERO_WORDS[wordIdx]}
            </h2>
          </div>

          {/* Search bar */}
          <div className="mt-10 md:mt-14 w-full max-w-4xl">
            <div className="bg-white rounded-2xl md:rounded-full shadow-2xl px-2 py-2 md:pl-4 flex flex-col md:flex-row items-stretch md:items-center gap-2">
              {/* Location */}
              <div className="flex items-center gap-3 px-3 md:px-2 py-2 flex-1 min-w-0">
                <MapPin className="h-5 w-5 text-[#1F5B63] shrink-0" />
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Location
                  </p>
                  <Select value={location} onValueChange={setLocation}>
                    <SelectTrigger className="border-0 shadow-none p-0 h-6 text-sm font-medium text-slate-800 focus:ring-0">
                      <SelectValue placeholder="Where are you going?" />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATIONS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="hidden md:block w-px h-8 bg-slate-200" />

              {/* Date From */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-3 px-3 md:px-2 py-2 flex-1 text-left hover:bg-slate-50 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-[#1F5B63] shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        Check in
                      </p>
                      <p className="text-sm font-medium text-slate-800">
                        {dateFrom ? format(dateFrom, "dd MMM yyyy") : "Add date"}
                      </p>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
                </PopoverContent>
              </Popover>

              <ArrowRight className="hidden md:block h-4 w-4 text-slate-400" />

              {/* Date To */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-3 px-3 md:px-2 py-2 flex-1 text-left hover:bg-slate-50 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-[#1F5B63] shrink-0" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        Check out
                      </p>
                      <p className="text-sm font-medium text-slate-800">
                        {dateTo ? format(dateTo, "dd MMM yyyy") : "Add date"}
                      </p>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
                </PopoverContent>
              </Popover>

              <Button
                className="h-12 md:h-14 rounded-full px-8 text-white font-semibold text-base"
                style={{ backgroundColor: "#1F5B63" }}
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-[#1F5B63]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s, i) => {
            const Icon = STAT_ICONS[i];
            return (
              <div
                key={s.label}
                className="flex items-center gap-3 text-white justify-center md:justify-start"
              >
                <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-lg font-extrabold leading-none">{s.value}</p>
                  <p className="text-[12px] text-white/80">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlide {
          0% { opacity: 0; transform: translateY(24px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-24px); }
        }
      `}</style>
    </section>
  );
}
